import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import useToast from '../../components/molecules/Toast/useToast';
import { useDetailTabsMutation, DETAIL_TABS_QUERY_KEY, getDetailTabsMutationKey } from './detailQueries';

/**
 * @typedef {object} DetailTabsSaveVariables
 * @property {DetailTabItem[]} nextTabs
 * @property {boolean} [optimistic=false] - Patch GET cache before POST (close tab only)
 */

/**
 * @param {import('@tanstack/react-query').QueryKey} queryKey
 * @param {string} contexte
 * @returns {boolean}
 */
const isDetailTabsQueryForContexte = (queryKey, contexte) => (
  queryKey[0] === DETAIL_TABS_QUERY_KEY
  && typeof queryKey[1] === 'string'
  && queryKey[2] === contexte
);

/**
 * Patch all cached detail tabs for a context (tabs config is shared across record pages).
 * @param {import('@tanstack/react-query').QueryClient} queryClient
 * @param {string} contexte
 * @param {DetailTabItem[]} nextTabs
 */
const patchAllDetailTabsCacheForContexte = (queryClient, contexte, nextTabs) => {
  queryClient.setQueriesData(
    {
      predicate: (query) => isDetailTabsQueryForContexte(query.queryKey, contexte),
    },
    (old) => {
      if (!old || typeof old !== 'object') return { tabs: nextTabs };
      return { ...old, tabs: nextTabs };
    },
  );
};

/**
 * POST detail tabs configuration. Optimistic cache updates only when `optimistic: true`.
 * @param {string} path
 * @param {string} contexte
 * @param {import('@tanstack/react-query').UseMutationOptions<DetailTabsResponse, Error, DetailTabsSaveVariables, unknown>} [options = {}]
 * @returns {import('@tanstack/react-query').UseMutationResult<DetailTabsResponse, Error, DetailTabsSaveVariables, unknown>}
 */
export const useDetailTabsSaveMutation = (path, contexte, options = {}) => {
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const { t } = useTranslation();

  const {
    onSuccess, onError, onSettled, ...restOptions
  } = options;

  // Includes the mutation currently settling
  const isLastPendingSave = () => queryClient.isMutating({
    mutationKey: getDetailTabsMutationKey(contexte),
  }) === 1;

  return useDetailTabsMutation(path, contexte, {
    ...restOptions,
    onMutate: async (variables) => {
      if (!variables.optimistic) return {};

      const { nextTabs } = variables;

      await queryClient.cancelQueries({
        predicate: (query) => isDetailTabsQueryForContexte(query.queryKey, contexte),
      });

      const previous = queryClient.getQueriesData({
        predicate: (query) => isDetailTabsQueryForContexte(query.queryKey, contexte),
      });

      patchAllDetailTabsCacheForContexte(queryClient, contexte, nextTabs);

      return { previous };
    },
    onError: (error, variables, context) => {
      // Rollback only when no later save is queued: it would restore an intermediate tree
      // (closed tab flickering back). Otherwise the final invalidation reconciles with the server.
      if (variables.optimistic && isLastPendingSave()) {
        const previous = /** @type {{ previous?: Array<[import('@tanstack/react-query').QueryKey, unknown]> } | undefined} */ (
          context
        )?.previous;
        previous?.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }

      notify({
        message: error?.message || t('toast.general.error'),
        type: 'alert',
      });
      onError?.(error, variables, context);
    },
    onSettled: (data, error, variables, context) => {
      // Refetch only after the last queued save: an intermediate GET would bring back tabs
      // closed in the meantime.
      if (isLastPendingSave()) {
        queryClient.invalidateQueries({
          predicate: (query) => isDetailTabsQueryForContexte(query.queryKey, contexte),
        });
      }
      onSettled?.(data, error, variables, context);
    },
    onSuccess: (data, variables, context) => {
      if (!variables.optimistic) {
        patchAllDetailTabsCacheForContexte(queryClient, contexte, variables.nextTabs);
      }
      onSuccess?.(data, variables, context);
    },
  });
};
